// src/data/emailFields.ts
// -----------------------
// Write-layer email guard for the repositories (email-validation-plan §6B). Every
// repo write that carries an email field runs it through here, so an address is
// always STORED normalised (trimmed + lowercased), a blank optional email is stored
// as NULL (never ''), and an unsendable address is refused with a plain-English
// error the caller already knows how to surface ({ error: { message } }). This holds
// even when a screen forgets its own client-side check, and it means the later DB
// CHECK constraint (Branch 2) never sees a value it would reject.
//
// Only fields PRESENT on the object are touched — a partial update that doesn't
// mention the email leaves the stored value alone.

import { isValidEmail, normalizeEmail, toNullableEmail } from "../utils/sanitize";

export type EmailFieldError = { message: string };

export function invalidEmailMessage(value: unknown): string {
  return `"${String(value ?? "").trim()}" is not a valid email address. Please check it and try again.`;
}

/** Normalise the named email fields on a single row (a shallow copy is returned).
 *  Returns an error — and the row unchanged — if a present, non-blank value is invalid. */
export function cleanEmailFields<T extends object>(
  row: T,
  fields: readonly string[]
): { row: T; error: EmailFieldError | null } {
  if (!row || typeof row !== "object") return { row, error: null };
  const src = row as Record<string, unknown>;
  const out: Record<string, unknown> = { ...src };
  for (const f of fields) {
    if (!(f in src) || src[f] === undefined) continue;
    const email = toNullableEmail(src[f]);
    if (email !== null && !isValidEmail(email)) {
      return { row, error: { message: invalidEmailMessage(src[f]) } };
    }
    out[f] = email;
  }
  return { row: out as T, error: null };
}

/** Array form (bulk inserts): the first invalid row's error wins. */
export function cleanEmailFieldsAll<T extends object>(
  rows: readonly T[],
  fields: readonly string[]
): { rows: T[]; error: EmailFieldError | null } {
  const out: T[] = [];
  for (const r of rows ?? []) {
    const res = cleanEmailFields(r, fields);
    if (res.error) return { rows: [...(rows ?? [])], error: res.error };
    out.push(res.row);
  }
  return { rows: out, error: null };
}

/** A list of addresses (e.g. requests.cc_emails): normalised, de-duplicated, with blank
 *  and unsendable entries dropped. Never throws; a non-array becomes []. */
export function cleanEmailList(list: unknown): string[] {
  if (!Array.isArray(list)) return [];
  const seen = new Set<string>();
  for (const v of list) {
    const e = normalizeEmail(v);
    if (e && isValidEmail(e)) seen.add(e);
  }
  return [...seen];
}

// ── Case-insensitive email LOOKUPS (email-validation-plan §7.2) ──────────────
// Stored addresses still carry mixed case until the Branch 2 data clean-up runs, so an
// exact `.eq("email", x)` can miss the right row. Lookups use `.ilike(col, pattern)`
// instead — which works both BEFORE and AFTER the clean-up — and then re-check every
// candidate with sameEmail(), because an ILIKE pattern is not an exact match:
// PostgREST also treats `*` as a wildcard. Never trust the pattern alone.

/** An ILIKE pattern for `email`: normalised, with the LIKE wildcards (% and _) and the
 *  escape character escaped so they match literally. */
export function emailIlikePattern(email: unknown): string {
  return normalizeEmail(email).replace(/[\\%_]/g, "\\$&");
}

/** True when two addresses are the same after normalising (trim + lowercase). Blank
 *  never matches anything. */
export function sameEmail(a: unknown, b: unknown): boolean {
  const x = normalizeEmail(a);
  return x !== "" && x === normalizeEmail(b);
}
